/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Button, TextField, Typography2 } from '@coursera/cds-core';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SettingsModal from 'bundles/account-settings/components/common/SettingsModal';
import FormError from 'bundles/account-settings/components/sections/two-factor/FormError';

import _t from 'i18n!nls/account-settings';

type Props = {
  updating: boolean;
  invalidFields: string[];
  qrCode: string;
};

const styles = {
  actions: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-150);
    align-items: end;
  `,
  // Overrides from parent cascading styles
  input: css`
    label + div {
      margin-top: 0 !important;
    }

    input {
      margin-bottom: 0 !important;
      border: none !important;
    }
  `,
  // Overrides from parent cascading styles
  button: css`
    height: 41px !important;
    border-radius: var(--cds-border-radius-50) !important;
  `,
  bodyText: css`
    p {
      margin-bottom: var(--cds-spacing-150);
    }

    a {
      text-decoration: underline;
    }
  `,
};

const CodeConfirmationModal = ({ invalidFields, updating, qrCode }: Props) => {
  const [code, setCode] = useState('');

  const handleSubmit = () => {
    SettingsActionCreators.confirmTwoFactorAuthenticationCode(code);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value.trim());
  };

  const handleClose = () => {
    SettingsActionCreators.cancelTwoFactorAuthenticationAction();
  };

  return (
    <div className="rc-ConfirmTwoFactorCodes">
      <SettingsModal title={_t('Enable Two-Factor Authentication')} onClose={handleClose}>
        <div className="bt3-row">
          <div className="bt3-col-md-4">
            <img
              className="qr-code"
              src={qrCode}
              alt={_t('QR Code. Scan with a two-factor authentication mobile app to enable two factor authentication')}
            />
          </div>
          <div className="bt3-col-md-8 code-input" css={styles.bodyText}>
            <Typography2 variant="bodyPrimary" component="p">
              <FormattedHTMLMessage
                message={_t(
                  `Using your favorite two-factor authentication mobile app (we recommend
                    <a href="https://support.google.com/accounts/answer/1066447"
                       target="_blank">Google Authenticator</a>),
                    scan the barcode displayed on the screen.`
                )}
              />
            </Typography2>
            <Typography2 variant="bodyPrimary" component="p">
              <FormattedHTMLMessage
                message={_t(
                  `Once your app recognizes your Coursera account,
                    enter the code it shows for your account and click "Verify Code"`
                )}
              />
            </Typography2>
            <div className="code-form">
              <FormError fields={invalidFields} />
              <div css={styles.actions}>
                <TextField
                  css={styles.input}
                  label={_t('Two-Factor Code')}
                  id="settings-two-factor-code"
                  placeholder="123456"
                  onChange={handleChange}
                  value={code}
                />
                <Button variant="primary" onClick={handleSubmit} disabled={updating} css={styles.button}>
                  {_t('Verify Code')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SettingsModal>
    </div>
  );
};

export default CodeConfirmationModal;
